import { useState } from 'react'
import { ChevronRight, Pencil, Trash2, Check, X, Bot, FileText, Loader2, Globe, Sparkles } from 'lucide-react'
import type { Competitor, BriefScope, PricingModel } from '@ispot/shared'

function CompetitorLogo({ name, logoUrl, size = 28 }: { name: string; logoUrl?: string; size?: number }) {
  const [imgError, setImgError] = useState(false)
  const initial = name.charAt(0).toUpperCase()

  if (!logoUrl || imgError) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%', background: 'var(--color-accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        fontSize: `${Math.round(size * 0.42)}px`, fontWeight: 600, color: 'white',
      }}>
        {initial}
      </div>
    )
  }

  return (
    <img
      src={logoUrl}
      alt={name}
      onError={() => setImgError(true)}
      style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        border: '1px solid var(--color-border-subtle)',
      }}
    />
  )
}

/** Semantic status tokens — paired bg/fg, intentionally off domain hues */
const TYPE_TOKENS: Record<Competitor['type'], { bg: string; fg: string }> = {
  direct:    { bg: 'var(--color-danger-bg)',  fg: 'var(--color-danger-fg)' },
  adjacent:  { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-fg)' },
  potential: { bg: 'var(--color-info-bg)',    fg: 'var(--color-info-fg)' },
}

const PRICING_MODEL_LABEL: Record<PricingModel, string> = {
  seat: 'Per seat',
  usage: 'Usage-based',
  flat: 'Flat rate',
  freemium: 'Freemium',
  unknown: '',
}

/** Extract a price range string from pricingTiers like ["Free", "Pro: $49/mo", "Team: $99/mo"]. */
function summarizePricingRange(tiers?: string[]): string | null {
  if (!tiers || tiers.length === 0) return null
  const prices: number[] = []
  let hasFree = false
  for (const tier of tiers) {
    if (/\bfree\b/i.test(tier)) hasFree = true
    const matches = tier.match(/\$\s?([\d,]+(?:\.\d+)?)/g)
    if (matches) {
      for (const m of matches) {
        const num = parseFloat(m.replace(/[$,\s]/g, ''))
        if (Number.isFinite(num) && num > 0) prices.push(num)
      }
    }
  }
  if (prices.length === 0) return hasFree ? 'Free' : null
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const fmt = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(0)}`)
  if (min === max) return hasFree ? `Free–${fmt(max)}` : fmt(min)
  return `${hasFree ? 'Free' : fmt(min)}–${fmt(max)}`
}

/** Split a comma-separated string into trimmed bullet items */
function toBullets(text?: string): string[] {
  if (!text) return []
  return text.split(',').map(s => s.trim()).filter(Boolean)
}

interface CompetitorTableRowProps {
  competitor: Competitor
  visibleColumns: Record<string, boolean>
  onUpdate: (id: string, data: Partial<Competitor>) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onConfirmSuggestion: (id: string) => Promise<void>
  onOpenBrief: (scope: BriefScope) => void
  onEnrich?: () => Promise<void>
}

export function CompetitorTableRow({
  competitor,
  visibleColumns,
  onUpdate,
  onDelete,
  onConfirmSuggestion,
  onOpenBrief,
  onEnrich,
}: CompetitorTableRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Partial<Competitor>>({})
  const [localEnriching, setLocalEnriching] = useState(false)

  const isEnriching = localEnriching
    || competitor.enrichmentStatus === 'enriching'
  const enrichmentFailed = competitor.enrichmentStatus === 'failed'
  const strengthBullets = toBullets(competitor.strengths)
  const gapBullets = toBullets(competitor.gaps)

  async function handleEnrich() {
    if (!onEnrich || isEnriching) return
    setLocalEnriching(true)
    try {
      await onEnrich()
    } finally {
      setLocalEnriching(false)
    }
  }

  function startEdit() {
    setDraft({
      name: competitor.name,
      category: competitor.category,
      type: competitor.type,
      url: competitor.url,
      whatTheyDo: competitor.whatTheyDo,
      strengths: competitor.strengths,
      gaps: competitor.gaps,
      notes: competitor.notes,
      investors: competitor.investors,
      hq: competitor.hq,
      isAiFirst: competitor.isAiFirst,
    })
    setEditing(true)
    if (!expanded) setExpanded(true)
  }

  async function saveEdit() {
    await onUpdate(competitor.id, draft)
    setEditing(false)
  }

  function cancelEdit() {
    setEditing(false)
    setDraft({})
  }

  // Count visible columns for colspan
  const baseColCount = 2 // name + category always visible
  const toggledCols = Object.keys(visibleColumns).filter(k => visibleColumns[k] && k !== 'name' && k !== 'category').length
  const totalCols = baseColCount + toggledCols + 2 // +2 for brief + actions

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '13px',
    color: 'var(--color-text)',
    borderBottom: expanded ? 'none' : '1px solid var(--color-border-subtle)',
    verticalAlign: 'middle',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    fontSize: '13px',
    background: 'white',
    color: 'var(--color-text)',
    boxSizing: 'border-box' as const,
  }

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
    color: 'var(--color-text-muted)',
    marginBottom: '6px',
    display: 'block',
  }

  /**
   * Render strengths/gaps as neutral outlined chips.
   * Sentiment is encoded only in the leading dot (success / warning).
   * Matches the Pill component shape used in CompetitorBriefRow, but neutral.
   */
  function chipPreview(items: string[], dotColor: string) {
    if (items.length === 0) {
      if (isEnriching) {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-text-muted)', fontSize: 12, fontStyle: 'italic' }}>
            <Loader2 size={11} style={{ animation: 'spin 1s linear infinite', color: dotColor }} />
            Enriching…
          </span>
        )
      }
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>
    }
    const shown = items.slice(0, 2)
    const more = items.length - shown.length
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {shown.map((item, i) => (
          <span
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: '4px 10px',
              borderRadius: '999px',
              fontSize: '12px',
              lineHeight: 1.25,
              fontWeight: 450,
              background: 'transparent',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border-subtle)',
              maxWidth: '100%',
            }}
          >
            <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: '999px', background: dotColor, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item}</span>
          </span>
        ))}
        {more > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 10px',
              borderRadius: '999px',
              fontSize: '12px',
              lineHeight: 1.25,
              fontWeight: 450,
              color: 'var(--color-text-muted)',
              border: '1px dashed var(--color-border)',
            }}
          >
            +{more} more
          </span>
        )}
      </div>
    )
  }

  /** Full bullet list for expanded panel */
  function bulletList(items: string[], color: string) {
    if (items.length === 0) return <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>None listed</span>
    return (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '13px', lineHeight: '20px', color: 'var(--color-text)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0, marginTop: '7px' }} />
            {item}
          </li>
        ))}
      </ul>
    )
  }

  function cell(key: keyof Competitor) {
    if (!visibleColumns[key] && key !== 'name') return null
    const value = competitor[key]
    if (typeof value === 'boolean') return <td style={tdStyle}>{value ? '✓' : '—'}</td>
    if (!value && isEnriching) {
      return (
        <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontStyle: 'italic' }}>
            <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
            Enriching…
          </span>
        </td>
      )
    }
    return <td style={{ ...tdStyle, color: value ? 'var(--color-text)' : 'var(--color-text-muted)' }}>{(value as string) || '—'}</td>
  }

  const rowBg = competitor.suggestedByAi ? 'rgba(232,67,0,0.04)' : 'transparent'
  const hoverBg = 'var(--color-warm-50)'

  return (
    <>
      {/* Compact row */}
      <tr
        style={{ background: rowBg, cursor: 'pointer', transition: 'background 0.15s' }}
        onClick={() => { if (!editing) setExpanded(e => !e) }}
        onMouseEnter={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = hoverBg }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = rowBg }}
      >
        {/* Name */}
        <td style={{ ...tdStyle, maxWidth: '220px', fontWeight: 500 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChevronRight
              size={14}
              style={{
                flexShrink: 0,
                color: 'var(--color-text-muted)',
                transition: 'transform 0.2s',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            />
            <CompetitorLogo name={competitor.name} logoUrl={competitor.logoUrl} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{competitor.name}</span>
            {competitor.suggestedByAi && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '2px 7px',
                  background: 'var(--color-accent-50)',
                  color: 'var(--color-accent)',
                  borderRadius: '999px',
                  fontWeight: 500,
                  flexShrink: 0,
                }}
              >
                Suggested
              </span>
            )}
          </div>
        </td>

        {/* Category */}
        <td style={tdStyle}>
          {competitor.category ? (
            <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{competitor.category}</span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
          )}
        </td>

        {/* Type */}
        {visibleColumns.type !== false && (
          <td style={tdStyle}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '10.5px',
                letterSpacing: '0.04em',
                padding: '3px 10px',
                borderRadius: '999px',
                background: TYPE_TOKENS[competitor.type].bg,
                color: TYPE_TOKENS[competitor.type].fg,
                fontWeight: 500,
                textTransform: 'lowercase',
              }}
            >
              {competitor.type}
            </span>
          </td>
        )}

        {/* URL */}
        {visibleColumns.url !== false && (
          <td style={tdStyle} onClick={e => e.stopPropagation()}>
            {competitor.url
              ? <a href={competitor.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <Globe size={12} style={{ flexShrink: 0 }} />{competitor.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                </a>
              : <span style={{ color: 'var(--color-text-muted)' }}>—</span>
            }
          </td>
        )}

        {/* Pricing - range + model caption (data from pricingTiers + pricingModel) */}
        {visibleColumns.pricing !== false && (
          (() => {
            const range = summarizePricingRange(competitor.pricingTiers)
            const modelLabel = competitor.pricingModel && competitor.pricingModel !== 'unknown'
              ? PRICING_MODEL_LABEL[competitor.pricingModel]
              : ''
            if (!range && !modelLabel) {
              if (isEnriching) {
                return (
                  <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontStyle: 'italic' }}>
                      <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                      Enriching…
                    </span>
                  </td>
                )
              }
              return <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>—</td>
            }
            return (
              <td style={tdStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {range && (
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px', color: 'var(--color-text)', whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>
                      {range}
                    </span>
                  )}
                  {modelLabel && (
                    <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', letterSpacing: '0.02em' }}>
                      {modelLabel}
                    </span>
                  )}
                </div>
              </td>
            )
          })()
        )}

        {/* What They Do - truncated */}
        {visibleColumns.whatTheyDo !== false && (
          <td style={{ ...tdStyle, maxWidth: '240px' }}>
            {competitor.whatTheyDo ? (
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{competitor.whatTheyDo}</span>
            ) : isEnriching ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontStyle: 'italic', color: 'var(--color-text-muted)' }}>
                <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                Enriching…
              </span>
            ) : (
              <span style={{ color: 'var(--color-text-muted)' }}>—</span>
            )}
          </td>
        )}

        {/* Strengths - neutral chip with success-colored dot */}
        {visibleColumns.strengths !== false && (
          <td style={{ ...tdStyle, maxWidth: '260px', whiteSpace: 'normal', overflow: 'visible', verticalAlign: 'top', padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
            {chipPreview(strengthBullets, 'var(--color-success)')}
          </td>
        )}

        {/* Gaps - neutral chip with warning-colored dot */}
        {visibleColumns.gaps !== false && (
          <td style={{ ...tdStyle, maxWidth: '260px', whiteSpace: 'normal', overflow: 'visible', verticalAlign: 'top', padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
            {chipPreview(gapBullets, 'var(--color-warning)')}
          </td>
        )}

        {/* Notes */}
        {visibleColumns.notes !== false && cell('notes')}

        {/* Investors */}
        {visibleColumns.investors === true && cell('investors')}

        {/* HQ */}
        {visibleColumns.hq === true && cell('hq')}

        {/* AI-First */}
        {visibleColumns.isAiFirst !== false && (
          <td style={tdStyle}>
            {competitor.isAiFirst ? <Bot size={14} style={{ color: 'var(--color-accent)' }} /> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
          </td>
        )}

        {/* Brief */}
        <td style={tdStyle} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onOpenBrief({ type: 'competitor', competitorId: competitor.id })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px', display: 'flex', alignItems: 'center' }}
            title="Open brief"
          >
            <FileText size={14} />
          </button>
        </td>

        {/* Actions */}
        <td style={{ ...tdStyle, maxWidth: '100px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {competitor.suggestedByAi && (
              <button onClick={() => onConfirmSuggestion(competitor.id)} title="Confirm suggestion" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', padding: '2px' }}><Check size={14} /></button>
            )}
            {onEnrich && (
              <button
                onClick={handleEnrich}
                disabled={isEnriching}
                title={isEnriching ? 'Enriching…' : enrichmentFailed ? `Re-enrich (last attempt failed${competitor.enrichmentError ? `: ${competitor.enrichmentError}` : ''})` : 'Re-enrich: find URL, scrape, generate brief, derive fields'}
                style={{
                  background: 'none', border: 'none',
                  cursor: isEnriching ? 'default' : 'pointer',
                  color: enrichmentFailed ? 'var(--color-error)' : 'var(--color-accent)',
                  padding: '2px',
                  opacity: isEnriching ? 0.5 : 1,
                }}
              >
                {isEnriching ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={13} />}
              </button>
            )}
            <button onClick={startEdit} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px' }}><Pencil size={13} /></button>
            <button onClick={() => onDelete(competitor.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px' }}><Trash2 size={13} /></button>
          </div>
        </td>
      </tr>

      {/* Expanded detail panel */}
      {expanded && (
        <tr style={{ background: 'var(--color-warm-50)' }}>
          <td colSpan={totalCols} style={{ padding: 0, borderBottom: '1px solid var(--color-border-subtle)' }}>
            <div style={{
              padding: '20px 24px 20px 48px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '20px',
              borderTop: '1px solid var(--color-border-subtle)',
            }}>
              {/* Column 1: Description + Metadata */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>What They Do</label>
                  {editing ? (
                    <textarea
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                      value={(draft.whatTheyDo as string) ?? ''}
                      onChange={e => setDraft(d => ({ ...d, whatTheyDo: e.target.value }))}
                    />
                  ) : (
                    <p style={{ fontSize: '13px', lineHeight: '20px', color: 'var(--color-text-secondary)', margin: 0 }}>
                      {competitor.whatTheyDo || <span style={{ color: 'var(--color-text-muted)' }}>No description</span>}
                    </p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Notes</label>
                  {editing ? (
                    <textarea
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                      value={(draft.notes as string) ?? ''}
                      onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                    />
                  ) : (
                    <p style={{ fontSize: '13px', lineHeight: '20px', color: 'var(--color-text-secondary)', margin: 0 }}>
                      {competitor.notes || <span style={{ color: 'var(--color-text-muted)' }}>No notes</span>}
                    </p>
                  )}
                </div>

                {editing && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Category</label>
                      <input style={inputStyle} value={(draft.category as string) ?? ''} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Type</label>
                      <select style={inputStyle} value={draft.type ?? competitor.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value as Competitor['type'] }))}>
                        <option value="direct">Direct</option>
                        <option value="adjacent">Adjacent</option>
                        <option value="potential">Potential</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>URL</label>
                      <input style={inputStyle} value={(draft.url as string) ?? ''} onChange={e => setDraft(d => ({ ...d, url: e.target.value }))} placeholder="https://" />
                    </div>
                    <div>
                      <label style={labelStyle}>Name</label>
                      <input style={inputStyle} value={(draft.name as string) ?? ''} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Investors</label>
                      <input style={inputStyle} value={(draft.investors as string) ?? ''} onChange={e => setDraft(d => ({ ...d, investors: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>HQ</label>
                      <input style={inputStyle} value={(draft.hq as string) ?? ''} onChange={e => setDraft(d => ({ ...d, hq: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="checkbox" checked={!!(draft.isAiFirst)} onChange={e => setDraft(d => ({ ...d, isAiFirst: e.target.checked }))} style={{ width: '16px', height: '16px', accentColor: 'var(--color-accent)' }} />
                      <label style={{ fontSize: '13px', color: 'var(--color-text)' }}>AI-First</label>
                    </div>
                  </div>
                )}
              </div>

              {/* Column 2: Strengths */}
              <div>
                <label style={labelStyle}>Strengths</label>
                {editing ? (
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                    value={(draft.strengths as string) ?? ''}
                    onChange={e => setDraft(d => ({ ...d, strengths: e.target.value }))}
                    placeholder="Comma-separated strengths"
                  />
                ) : (
                  bulletList(strengthBullets, 'var(--color-accent)')
                )}
              </div>

              {/* Column 3: Gaps */}
              <div>
                <label style={labelStyle}>Gaps / Weaknesses</label>
                {editing ? (
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                    value={(draft.gaps as string) ?? ''}
                    onChange={e => setDraft(d => ({ ...d, gaps: e.target.value }))}
                    placeholder="Comma-separated gaps or weaknesses"
                  />
                ) : (
                  bulletList(gapBullets, 'var(--color-error)')
                )}
              </div>
            </div>

            {/* Expanded panel footer with actions */}
            <div style={{
              padding: '10px 24px 14px 48px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              borderTop: '1px solid var(--color-border-subtle)',
              background: 'var(--color-warm-50)',
            }}>
              {editing ? (
                <>
                  <button onClick={cancelEdit} style={{
                    padding: '6px 14px', background: 'none', border: '1px solid var(--color-border)',
                    borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: 'var(--color-text-secondary)',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <X size={13} /> Cancel
                  </button>
                  <button onClick={saveEdit} style={{
                    padding: '6px 14px', background: 'var(--color-accent)', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <Check size={13} /> Save Changes
                  </button>
                </>
              ) : (
                <>
                  <button onClick={(e) => { e.stopPropagation(); onOpenBrief({ type: 'competitor', competitorId: competitor.id }) }} style={{
                    padding: '6px 14px', background: 'none', border: '1px solid var(--color-border)',
                    borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: 'var(--color-text-secondary)',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <FileText size={13} /> View Brief
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); startEdit() }} style={{
                    padding: '6px 14px', background: 'var(--color-warm-100)', border: '1px solid var(--color-border)',
                    borderRadius: '8px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', color: 'var(--color-text)',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <Pencil size={13} /> Edit
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
